/**
 * Bottom slideshow custom element
 * - Auto-rotate with configurable interval (pauses on hover/focus)
 * - Prev/Next navigation buttons
 * - Dot indicators
 * - Pauses when user is interacting or page not visible
 */
class BottomSlideshow extends HTMLElement {
  constructor() {
    super();
    this.slides = Array.from(this.querySelectorAll('.bottom-slideshow__slide'));
    this.dots = Array.from(this.querySelectorAll('.bottom-slideshow__dot'));
    this.prevBtn = this.querySelector('[data-action="prev"]');
    this.nextBtn = this.querySelector('[data-action="next"]');
    this.currentIndex = 0;
    this.totalSlides = this.slides.length;
    this.autoRotate = this.getAttribute('data-auto-rotate') === 'true';
    this.rotateMs = (parseInt(this.getAttribute('data-rotate-seconds'), 10) || 6) * 1000;
    this.timer = null;
    this.isPaused = false;
  }

  connectedCallback() {
    if (this.totalSlides < 2) return;

    // スライドの総数を CSS 変数に渡す
    this.style.setProperty('--total-slides', this.totalSlides);

    // ナビゲーションボタン
    if (this.prevBtn) {
      this.prevBtn.addEventListener('click', () => this.go(-1));
    }
    if (this.nextBtn) {
      this.nextBtn.addEventListener('click', () => this.go(1));
    }

    // ドット
    this.dots.forEach((dot) => {
      dot.addEventListener('click', (e) => {
        const idx = parseInt(e.currentTarget.dataset.goTo, 10);
        this.goTo(idx);
      });
    });

    // ホバー / フォーカス時に一時停止
    this.addEventListener('mouseenter', () => this.pause());
    this.addEventListener('mouseleave', () => this.resume());
    this.addEventListener('focusin', () => this.pause());
    this.addEventListener('focusout', () => this.resume());

    // タブが非表示になったら停止
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        this.pause();
      } else {
        this.resume();
      }
    });

    // キーボード対応
    this.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowLeft') {
        this.go(-1);
      } else if (e.key === 'ArrowRight') {
        this.go(1);
      }
    });

    // タッチスワイプ対応
    this.addTouchSupport();

    // 自動ローテーション開始
    if (this.autoRotate) {
      this.start();
    }
  }

  disconnectedCallback() {
    this.stop();
  }

  start() {
    this.stop();
    if (!this.autoRotate || this.isPaused) return;
    this.timer = setInterval(() => this.go(1), this.rotateMs);
  }

  stop() {
    if (this.timer) {
      clearInterval(this.timer);
      this.timer = null;
    }
  }

  pause() {
    this.isPaused = true;
    this.stop();
  }

  resume() {
    this.isPaused = false;
    if (this.autoRotate) {
      this.start();
    }
  }

  go(delta) {
    const next = (this.currentIndex + delta + this.totalSlides) % this.totalSlides;
    this.goTo(next);
  }

  goTo(index) {
    if (index === this.currentIndex || index < 0 || index >= this.totalSlides) return;

    this.slides[this.currentIndex].classList.remove('is-active');
    this.dots[this.currentIndex].classList.remove('is-active');
    this.dots[this.currentIndex].setAttribute('aria-selected', 'false');

    this.currentIndex = index;

    this.slides[this.currentIndex].classList.add('is-active');
    this.dots[this.currentIndex].classList.add('is-active');
    this.dots[this.currentIndex].setAttribute('aria-selected', 'true');

    // タイマーリセット (次のスライドまで full interval を確保)
    if (this.autoRotate && !this.isPaused) {
      this.start();
    }
  }

  addTouchSupport() {
    let startX = 0;
    let endX = 0;
    const threshold = 50;

    this.addEventListener(
      'touchstart',
      (e) => {
        startX = e.touches[0].clientX;
        this.pause();
      },
      { passive: true }
    );

    this.addEventListener(
      'touchend',
      (e) => {
        endX = e.changedTouches[0].clientX;
        const diff = startX - endX;
        if (Math.abs(diff) > threshold) {
          this.go(diff > 0 ? 1 : -1);
        }
        this.resume();
      },
      { passive: true }
    );
  }
}

if (!customElements.get('bottom-slideshow')) {
  customElements.define('bottom-slideshow', BottomSlideshow);
}
